import { createContext, useContext, useState, useCallback, useEffect, ReactNode } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { useMyProducers } from '@/hooks/useMyProducers';
import { useMyPortals } from '@/hooks/useMyPortals';
import { toast } from '@/hooks/use-toast';
import { useInlineBanner } from '@/context/InlineBannerContext';

// ── Public types ─────────────────────────────────────────────
/** Canonical modes – visitor / host / editor / admin */
export type AppMode = 'visitor' | 'host' | 'editor' | 'admin';

/** @deprecated Use AppMode instead */
export type PortalContext = 'customer' | 'producer' | 'admin';

// ── Mapping helpers between legacy route tokens and AppMode ──
const ROUTE_TOKEN_TO_MODE: Record<string, AppMode> = {
  customer: 'visitor',
  producer: 'host',
  editor: 'editor',
  admin: 'admin',
};

const MODE_TO_ROUTE_TOKEN: Record<AppMode, string> = {
  visitor: 'customer',
  host: 'producer',
  editor: 'editor',
  admin: 'admin',
};

/** Legacy PortalContext ↔ AppMode */
const PORTAL_TO_MODE: Record<PortalContext, AppMode> = {
  customer: 'visitor',
  producer: 'host',
  admin: 'admin',
};
const MODE_TO_PORTAL: Record<AppMode, PortalContext> = {
  visitor: 'customer',
  host: 'producer',
  editor: 'customer', // Legacy portal has no editor, fall back to customer
  admin: 'admin',
};

// ── Constants ────────────────────────────────────────────────
const STORAGE_KEY = 'farmly:activeMode';

const MODE_ROUTES: Record<AppMode, string> = {
  visitor: '/app/customer',
  host: '/app/producer',
  editor: '/app/editor',
  admin: '/app/admin/producers',
};

const MODE_LABELS: Record<AppMode, string> = {
  visitor: 'besökarläget',
  host: 'värdläget',
  editor: 'redaktionsläget',
  admin: 'adminläget',
};

// ── Context value ────────────────────────────────────────────
interface ActivePortalContextValue {
  /** Current mode */
  activeMode: AppMode;
  /** Switch mode – validates access, shows toast, navigates */
  setMode: (mode: AppMode) => void;
  /** Whether the user can access host mode */
  isHost: boolean;
  /** Whether the user can access editor mode */
  isEditor: boolean;
  /** Whether the user can access admin mode */
  isAdmin: boolean;
  /** First producer name for display */
  firstProducerName: string | null;
  /** Whether we're still determining capabilities */
  isLoading: boolean;

  // ── Legacy compatibility (consumed by AppTopbar etc.) ──────
  /** @deprecated Use activeMode */
  activePortal: PortalContext;
  /** @deprecated Use setMode */
  switchPortal: (portal: PortalContext) => void;
  /** @deprecated Use isHost */
  canAccessProducer: boolean;
  /** @deprecated Use isAdmin */
  canAccessAdmin: boolean;
}

const ActivePortalContext = createContext<ActivePortalContextValue | undefined>(undefined);

// ── Helpers ──────────────────────────────────────────────────
function getModeFromPath(pathname: string): AppMode | null {
  if (pathname.startsWith('/app/admin') || pathname.startsWith('/admin')) return 'admin';
  if (pathname.startsWith('/app/editor')) return 'editor';
  if (pathname.startsWith('/app/producer') || pathname.startsWith('/producer')) return 'host';
  if (pathname.startsWith('/app/customer') || pathname.startsWith('/account')) return 'visitor';
  return null;
}

function readPersistedMode(): AppMode | null {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored === 'visitor' || stored === 'host' || stored === 'editor' || stored === 'admin') return stored;
  } catch { /* ignore */ }
  return null;
}

function persistMode(mode: AppMode) {
  try { localStorage.setItem(STORAGE_KEY, mode); } catch { /* ignore */ }
}

// ── Provider ─────────────────────────────────────────────────
export function ActivePortalProvider({ children }: { children: ReactNode }) {
  const { isAdmin: authIsAdmin, isProducer, isLoading: authLoading, rolesLoading } = useAuth();
  const location = useLocation();
  const modeFromPath = getModeFromPath(location.pathname);
  const { hasProducers, producers, isLoading: producersLoading } = useMyProducers({ enabled: modeFromPath !== 'admin' });
  const { isEditor: rpcIsEditor, isLoading: portalsLoading } = useMyPortals();
  const navigate = useNavigate();
  const { showBanner } = useInlineBanner();

  // Initial mode: URL > localStorage > visitor
  const initialMode = getModeFromPath(location.pathname) ?? readPersistedMode() ?? 'visitor';
  const [activeMode, setActiveMode] = useState<AppMode>(initialMode);

  // Admin doesn't need producersLoading in the gate
  const isLoading = activeMode === 'admin' 
    ? authLoading || rolesLoading || portalsLoading 
    : authLoading || rolesLoading || producersLoading || portalsLoading;

  // Derive capabilities
  const isHost = isProducer || hasProducers;
  const isEditor = rpcIsEditor;
  const isAdmin = authIsAdmin;

  const firstProducerName = activeMode === 'admin' ? null : (producers[0]?.name || null);

  // Sync mode from URL changes (e.g. manual navigation)
  useEffect(() => {
    const modeFromPath = getModeFromPath(location.pathname);
    if (modeFromPath && modeFromPath !== activeMode) {
      setActiveMode(modeFromPath);
      persistMode(modeFromPath);
    }
  }, [location.pathname]);

  // Validate mode when roles finish loading – demote if access revoked
  useEffect(() => {
    if (isLoading) return;
    if (activeMode === 'host' && !isHost) {
      setActiveMode('visitor');
      persistMode('visitor');
    }
    if (activeMode === 'editor' && !isEditor) {
      setActiveMode('visitor');
      persistMode('visitor');
    }
    if (activeMode === 'admin' && !isAdmin) {
      setActiveMode('visitor');
      persistMode('visitor');
    }
  }, [isLoading, isHost, isEditor, isAdmin]);

  const setMode = useCallback((mode: AppMode) => {
    if (mode === activeMode) return;

    // Admin guard
    if (mode === 'admin' && !isAdmin) {
      toast({ title: 'Ingen åtkomst', description: 'Du har inte adminbehörighet.', variant: 'destructive' });
      return;
    }

    // Editor guard
    if (mode === 'editor' && !isEditor) {
      toast({ title: 'Ingen åtkomst', description: 'Du är inte tilldelad som platsredaktör.', variant: 'destructive' });
      return;
    }

    // Host guard – redirect to qualify wizard
    if (mode === 'host' && !isHost) {
      navigate('/for-producers/qualify');
      return; // no toast, no mode change
    }

    setActiveMode(mode);
    persistMode(mode);
    navigate(MODE_ROUTES[mode]);
    showBanner({ variant: 'info', message: `Du är nu i ${MODE_LABELS[mode]}` });
  }, [activeMode, isAdmin, isEditor, isHost, navigate, showBanner]);

  // Legacy adapter
  const switchPortal = useCallback((portal: PortalContext) => {
    setMode(PORTAL_TO_MODE[portal]);
  }, [setMode]);

  const value: ActivePortalContextValue = {
    activeMode,
    setMode,
    isHost,
    isEditor,
    isAdmin,
    firstProducerName,
    isLoading,
    // Legacy
    activePortal: MODE_TO_PORTAL[activeMode],
    switchPortal,
    canAccessProducer: isHost,
    canAccessAdmin: isAdmin,
  };

  return (
    <ActivePortalContext.Provider value={value}>
      {children}
    </ActivePortalContext.Provider>
  );
}

/** Primary hook */
export function useActiveMode() {
  const context = useContext(ActivePortalContext);
  if (!context) throw new Error('useActiveMode must be used within ActivePortalProvider');
  return context;
}

/** @deprecated Use useActiveMode() */
export function useActivePortal() {
  return useActiveMode();
}
